import { EntityBadge } from '@common/Copilot/CopilotAgentChat/EntityBadge';
import { Entity } from '@common/types/Entity';
import { PaperclipIcon } from '@prophecy/icons';
import { agentChatFileNames } from '@prophecy/shared-core/agentChat/fileContext';
import { theme } from '@prophecy/theme';
import { Button } from '@prophecy/ui-v3/Button';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Shimmer } from '@prophecy/ui-v3/Shimmer';
import { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { HOME_CHAT_TEST_IDS } from './constants';

import type { DataboxUploadItem } from '@prophecy/shared-core/databox/adapter';
import type { ChangeEvent } from 'react';

const HiddenFileInput = styled.input`
  display: none;
`;

function chipText(item: DataboxUploadItem) {
  if (item.status === 'error') return `${item.file.name} · upload failed`;
  if (item.status === 'done') return item.file.name;
  return `${item.file.name} · uploading…`;
}

export function AttachFilesButton({ onAdd, disabled }: { onAdd: (files: File[]) => void; disabled: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  // A second click() while a chooser is open makes Chrome queue another one and drop the first's files.
  const choosing = useRef(false);
  // Browsers without a file-input `cancel` event only show a dismissed chooser by refocusing the
  // window; deferred so a `change` dispatched after the focus still lands first.
  const settleOnFocus = useCallback(
    () =>
      setTimeout(() => {
        choosing.current = false;
      }),
    []
  );
  useEffect(() => {
    const input = inputRef.current;
    const settle = () => {
      choosing.current = false;
    };
    input?.addEventListener('cancel', settle);
    return () => {
      input?.removeEventListener('cancel', settle);
      window.removeEventListener('focus', settleOnFocus);
    };
  }, [settleOnFocus]);
  const openChooser = () => {
    if (choosing.current) return;
    choosing.current = true;
    window.addEventListener('focus', settleOnFocus, { once: true });
    inputRef.current?.click();
  };
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    choosing.current = false;
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length) onAdd(files);
  };
  return (
    <>
      <Button
        variant='tertiaryGray'
        size='s'
        aria-label='Attach files'
        icon={<PaperclipIcon type='default' />}
        disabled={disabled}
        onClick={openChooser}
        data-test-id={HOME_CHAT_TEST_IDS.attachButton}
      />
      <HiddenFileInput
        ref={inputRef}
        type='file'
        multiple
        onChange={onChange}
        data-test-id={HOME_CHAT_TEST_IDS.attachInput}
      />
    </>
  );
}

export function AttachmentChips({
  items,
  onRemove
}: {
  items: DataboxUploadItem[];
  onRemove: (itemId: string) => void;
}) {
  if (!items.length) return null;
  return (
    <Stack direction='horizontal' gap={theme.spaces.x8} wrap='wrap' data-test-id={HOME_CHAT_TEST_IDS.attachments}>
      {items.map((item) => (
        <Shimmer key={item.id} disableWhileLoading={false} loading={item.status !== 'done' && item.status !== 'error'}>
          <EntityBadge
            entityType={Entity.File}
            value={item.file.name}
            isMessageEntity
            badgeText={chipText(item)}
            showRemoveButton
            handleOnRemoveClick={() => onRemove(item.id)}
          />
        </Shimmer>
      ))}
    </Stack>
  );
}

/** The `@file:` entries a sent prompt carried, as chips above its text. */
export function PromptAttachments({ context }: { context: unknown }) {
  const names = agentChatFileNames(context);
  if (!names.length) return null;
  return (
    <Stack direction='horizontal' gap={theme.spaces.x8} wrap='wrap'>
      {names.map((name) => (
        <EntityBadge key={name} entityType={Entity.File} value={name} isMessageEntity badgeText={name} />
      ))}
    </Stack>
  );
}
